import { useSortable } from "@dnd-kit/sortable";
import { IconButton, ListItem } from "@mui/material";
import { styled } from "@mui/material/styles";
import { GripVertical, Plus, X } from "lucide-react";
import type React from "react";

const DraggableItem = styled(ListItem)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1, 2),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    "&:last-child": {
        marginBottom: 0,
    },
}));

const DragHandle = styled("div")({
    display: "flex",
    alignItems: "center",
    cursor: "grab",
    touchAction: "none",
});

interface SortableItemProps {
    id: string;
    children: React.ReactNode;
    onQuickAdd?: () => void;
    onRemove?: () => void;
    showAddButton?: boolean;
    showRemoveButton?: boolean;
}

export const SortableItem: React.FC<SortableItemProps> = ({
    id,
    children,
    onQuickAdd,
    onRemove,
    showAddButton = false,
    showRemoveButton = false,
}) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

    const style = transform
        ? {
              transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
              transition,
          }
        : undefined;

    const handleQuickAdd = (e: React.MouseEvent) => {
        e.stopPropagation();
        onQuickAdd?.();
    };

    const handleRemove = (e: React.MouseEvent) => {
        e.stopPropagation();
        onRemove?.();
    };

    return (
        <DraggableItem ref={setNodeRef} style={style}>
            <DragHandle {...attributes} {...listeners}>
                <GripVertical size={16} />
            </DragHandle>
            <span style={{ flex: 1 }}>{children}</span>
            {showAddButton && onQuickAdd && (
                <IconButton size="small" onClick={handleQuickAdd} sx={{ ml: 1 }}>
                    <Plus size={16} />
                </IconButton>
            )}
            {showRemoveButton && onRemove && (
                <IconButton size="small" onClick={handleRemove} sx={{ ml: 1 }} color="error">
                    <X size={16} />
                </IconButton>
            )}
        </DraggableItem>
    );
};
